import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import StatusBadge, { STATUS_LABELS } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Phone, DownloadSimple, Package, CheckCircle, Truck, Clock, CurrencyInr, X, Image as ImageIcon } from "@phosphor-icons/react";
import DesignActions from "@/components/DesignActions";
import PayOnlineDialog from "@/components/PayOnlineDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Two workflows — chosen based on project_type. Sheet-cutting is the default.
const FLOW_SHEET = [
  "new_order", "design_pending", "ready_to_cut",
  "in_cutting", "completed", "delivered",
];
const FLOW_GATE = [
  "new_order", "design_pending", "framing_making",
  "design_making", "design_cutting", "completed", "delivered",
];

// Customer-friendly stage descriptions (Hinglish so shop-floor customers instantly grok).
const STAGE_HINT = {
  new_order: "Order received. Team is preparing your design.",
  design_pending: "Design taiyaar ho gaya — please approve.",
  ready_to_cut: "Design approved. Machine par jane wala hai.",
  in_cutting: "Aapka maal CNC machine par cut ho raha hai.",
  framing_making: "Frame welding aur fabrication chal raha hai.",
  design_making: "Design ki cutting file taiyaar ho rahi hai.",
  design_cutting: "Panel/design cut ho raha hai.",
  completed: "Ready for delivery! Aap pickup / delivery schedule kar sakte hain.",
  delivered: "Delivered — dhanyavaad!",
};

const fmtWhen = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export default function TrackOrder() {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const isWs = order?.billing_mode === "wholesale";
  const [err, setErr] = useState("");
  const [payOpen, setPayOpen] = useState(false);
  const [designOpen, setDesignOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/track/${orderNumber}`);
        setOrder(data);
      } catch (e) {
        setErr(e.response?.status === 404 ? "Order not found" : "Unable to load order");
      }
    })();
  }, [orderNumber]);

  const downloadPdf = () => {
    window.open(`${API}/public/track/${orderNumber}/pdf`, "_blank");
  };

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="surface p-10 rounded-sm text-center max-w-md">
          <div className="w-12 h-12 stripe-accent mx-auto mb-4" />
          <div className="font-heading text-2xl font-black text-white">{err}</div>
          <div className="text-slate-400 mt-2 text-sm">
            Please verify the order number in your quotation.
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500 font-mono-num">
        Loading order…
      </div>
    );
  }

  const FLOW = order.project_type === "gate_railing" ? FLOW_GATE : FLOW_SHEET;
  const currentStep = FLOW.indexOf(order.status);
  const currentHint = STAGE_HINT[order.status] || "";
  const stageTs = order.stage_timestamps || {};
  const isApproved = order.design_approval_status === "confirmed";

  return (
    <div className="min-h-screen bp-grid" data-testid="public-track-page">
      <header className="border-b-2 border-orange-500/60 bg-slate-900/90 backdrop-blur">
        <div className="max-w-3xl mx-auto px-4 py-5 flex items-center gap-3">
          <img
            src="/logo.png"
            alt="Shobhit Creation"
            className="h-11 w-auto object-contain"
            data-testid="brand-logo"
          />
          <div>
            <div className="text-[11px] font-mono-num text-orange-400 tracking-widest">
              // ORDER TRACKING
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 md:p-6 space-y-6">
        {/* Hero */}
        <div className="surface p-6 rounded-sm">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="text-[10px] font-mono-num tracking-widest text-slate-500">ORDER</div>
              <div className="font-mono-num text-4xl md:text-5xl font-black text-orange-400 leading-none">
                #{order.order_number}
              </div>
              <div className="text-slate-200 mt-3 text-lg">{order.customer_name}</div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <StatusBadge status={order.status} size="lg" />
              <div className="text-xs text-slate-500 font-mono-num">
                Updated {fmtWhen(order.updated_at)}
              </div>
            </div>
          </div>

          {/* Current stage — the single most important line for the customer */}
          <div className="mt-5 rounded-sm border-l-4 border-orange-500 bg-slate-800/60 p-4" data-testid="current-stage-banner">
            <div className="text-[10px] font-mono-num tracking-widest text-orange-400">// AAPKA MAAL ABHI</div>
            <div className="font-heading text-2xl md:text-3xl font-black text-white mt-1">
              {STATUS_LABELS[order.status]?.toUpperCase() || String(order.status).replace(/_/g, " ").toUpperCase()}
            </div>
            {currentHint && (
              <div className="text-slate-300 text-sm mt-1.5">{currentHint}</div>
            )}
            {stageTs[order.status] && (
              <div className="text-[11px] font-mono-num text-cyan-300 mt-2 inline-flex items-center gap-1">
                <Clock size={12} weight="bold" /> Since {fmtWhen(stageTs[order.status])}
              </div>
            )}
          </div>

          {isApproved && (
            <div className="mt-3 rounded-sm border border-emerald-500/50 bg-emerald-500/10 p-3 space-y-3" data-testid="design-approved-banner">
              <div className="flex items-center gap-2 text-emerald-300 text-sm">
                <CheckCircle size={18} weight="fill" />
                <span><b>Design approved</b> — aapke confirm karne ke baad production start ho gaya hai.</span>
              </div>
              {order.design_preview && (
                <Button
                  onClick={() => setDesignOpen(true)}
                  className="w-full h-11 rounded-sm bg-slate-900/70 hover:bg-slate-800 text-emerald-200 border border-emerald-500/40 hover:border-emerald-400 font-heading tracking-widest"
                  data-testid="track-view-design-btn"
                >
                  <ImageIcon size={16} weight="fill" className="mr-2" />
                  VIEW MY DESIGN
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Timeline */}
        <div className="surface p-6 rounded-sm">
          <div className="text-xs font-mono-num tracking-widest text-slate-400 mb-4">// PROGRESS</div>
          <div className="space-y-3">
            {FLOW.map((s, i) => {
              const active = i <= currentStep;
              const isCurrent = i === currentStep;
              const done = i < currentStep;
              const ts = stageTs[s];
              return (
                <div key={s} className="flex items-start gap-3" data-testid={`timeline-step-${s}`}>
                  <div
                    className={
                      "w-8 h-8 flex items-center justify-center rounded-sm border shrink-0 " +
                      (isCurrent
                        ? "bg-orange-500 border-orange-500 text-white animate-pulse"
                        : done
                        ? "bg-emerald-500/80 border-emerald-500 text-white"
                        : active
                        ? "bg-orange-500 border-orange-500 text-white"
                        : "bg-slate-800 border-slate-700 text-slate-600")
                    }
                  >
                    {done ? <CheckCircle size={16} weight="fill" /> : isCurrent ? <span className="text-xs font-bold">•</span> : <span className="text-xs">{i + 1}</span>}
                  </div>
                  <div className="flex-1 pt-1">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <span
                        className={
                          "font-mono-num text-sm tracking-widest " +
                          (isCurrent ? "text-orange-400 font-bold" : done ? "text-emerald-300" : active ? "text-slate-200" : "text-slate-600")
                        }
                      >
                        {STATUS_LABELS[s]?.toUpperCase() || s.replace(/_/g, " ").toUpperCase()}
                      </span>
                      {isCurrent && (
                        <span className="text-[10px] font-mono-num text-orange-400 tracking-widest bg-orange-500/20 border border-orange-500/50 px-1.5 py-0.5 rounded-sm">
                          CURRENT
                        </span>
                      )}
                    </div>
                    {ts && (
                      <div className="text-[10px] font-mono-num text-slate-500 mt-0.5">
                        {done ? "Completed" : isCurrent ? "Started"  : ""} · {fmtWhen(ts)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Design preview for customer confirmation */}
        {order.design_preview && order.status === "design_pending" && (
          <div className="surface p-6 rounded-sm border-2 border-orange-500/40" data-testid="design-preview-block">
            <div className="text-xs font-mono-num tracking-widest text-orange-400 mb-3">
              // DESIGN PREVIEW — PLEASE REVIEW
            </div>
            <div className="rounded-sm overflow-hidden bg-slate-950 border border-slate-700 mb-4">
              {(order.design_preview.content_type || "").startsWith("image/") ? (
                <img
                  src={`${API}/public/track/${order.order_number}/design-preview`}
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                  alt="Design preview"
                  className="w-full max-h-[70vh] object-contain"
                  data-testid="design-preview-image"
                />
              ) : (
                <div className="p-8 text-center text-slate-400">
                  Design ready. Please open the PDF via the download button below.
                </div>
              )}
            </div>
            <div className="bg-amber-500/10 border border-amber-500/40 rounded-sm p-4 text-amber-200">
              <div className="font-heading text-base font-bold">Confirm the design</div>
            </div>
          </div>
        )}

        {/* Items — show item names + the raw sizes customer entered at order time */}
        <div className="surface p-6 rounded-sm">
          <div className="text-xs font-mono-num tracking-widest text-slate-400 mb-4 flex items-center gap-2">
            <Package size={14} /> // AAPKA ORDER
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-slate-500 font-mono-num text-[11px] tracking-widest">
                <tr className="border-b border-slate-700">
                  <th className="text-left py-2">#</th>
                  <th className="text-left">ITEM</th>
                  <th className="text-right">{isWs ? "PLATE SIZE" : "SIZE (W × H)"}</th>
                  <th className="text-right">QTY</th>
                  {isWs && <th className="text-right">WEIGHT</th>}
                  {isWs && <th className="text-right">RATE / KG</th>}
                  <th className="text-right">AMOUNT</th>
                </tr>
              </thead>
              <tbody className="font-mono-num">
                {order.items.map((it, i) => {
                  const w = it.width_in_raw ?? it.width_in;
                  const h = it.height_in_raw ?? it.height_in;
                  const isSqft = (it.unit || "sqft") === "sqft" || isWs;
                  return (
                    <tr key={i} className="border-b border-slate-800" data-testid={`track-item-row-${i}`}>
                      <td className="py-2 text-slate-500">{i + 1}</td>
                      <td>
                        <div className="text-slate-100">{it.item_name || it.thickness || "—"}</div>
                        {it.thickness && it.item_name && (
                          <div className="text-[10px] text-slate-500">{it.thickness}</div>
                        )}
                      </td>
                      <td className="text-right text-slate-300">
                        {isSqft && w && h ? (
                          <>
                            <span className="text-white font-bold">{w}″ × {h}″</span>
                            {!isWs && it.total_sqft > 0 && (
                              <span className="text-[10px] text-cyan-300 ml-1">({it.total_sqft} sqft)</span>
                            )}
                          </>
                        ) : (
                          <span className="text-slate-500 text-xs">—</span>
                        )}
                      </td>
                      <td className="text-right">{it.quantity}</td>
                      {isWs && <td className="text-right text-slate-300" data-testid={`track-item-weight-${i}`}>{it.weight_kg} kg</td>}
                      {isWs && <td className="text-right text-slate-300">₹{it.sale_rate_per_kg}/kg</td>}
                      <td className="text-right text-orange-400">₹{(it.total_amount || 0).toLocaleString("en-IN")}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-3 text-[11px] font-mono-num text-slate-500 flex items-center gap-2">
            <Truck size={12} /> Total: {isWs ? `${order.total_weight_kg} kg · ` : order.total_sqft > 0 ? `${order.total_sqft} sqft · ` : ""}₹{(order.total_amount || 0).toLocaleString("en-IN")}
          </div>
        </div>

        {/* Payment Summary */}
        <div className="surface p-6 rounded-sm">
          <div className="text-xs font-mono-num tracking-widest text-slate-400 mb-4">// PAYMENT SUMMARY</div>
          <div className="space-y-2 font-mono-num">
            <Row label="Total Amount" value={`₹${order.total_amount.toLocaleString("en-IN")}`} />
            <Row label="Advance Paid" value={`- ₹${order.advance_paid.toLocaleString("en-IN")}`} tone="cyan" />
            <div className="border-t border-slate-700 mt-2 pt-3">
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Balance Due</span>
                <span className="text-3xl font-bold text-white" data-testid="track-balance">
                  ₹{order.balance.toLocaleString("en-IN")}
                </span>
              </div>
              {order.balance > 0 && order.status !== "delivered" && (
                <Button
                  onClick={() => setPayOpen(true)}
                  className="mt-4 w-full h-14 rounded-sm bg-emerald-500 hover:bg-emerald-600 text-white font-heading tracking-widest shadow-lg shadow-emerald-500/20"
                  data-testid="track-pay-online-btn"
                >
                  <CurrencyInr size={22} weight="bold" className="mr-2" />
                  PAY DUE ₹{order.balance.toLocaleString("en-IN")} ONLINE
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Button
            onClick={downloadPdf}
            className="h-14 rounded-sm bg-slate-700 hover:bg-slate-600 text-white font-heading tracking-widest"
            data-testid="track-pdf-button"
          >
            <DownloadSimple size={20} className="mr-2" /> DOWNLOAD PDF BILL
          </Button>
          <Button
            asChild
            className="h-14 rounded-sm btn-industrial"
          >
            <a href="tel:+919122149006" data-testid="track-call-button">
              <Phone size={20} className="mr-2" /> CALL WORKSHOP
            </a>
          </Button>
        </div>

        <div className="text-center text-xs text-slate-600 font-mono-num pt-4">
          POWERED BY SHOBHIT CREATION · CNC LASER CUTTING
        </div>
      </main>

      <PayOnlineDialog
        open={payOpen}
        onClose={() => setPayOpen(false)}
        orderNumber={orderNumber}
        balance={order.balance}
      />

      <DesignViewerDialog
        open={designOpen}
        onClose={() => setDesignOpen(false)}
        orderNumber={orderNumber}
      />
    </div>
  );
}

function Row({ label, value, tone }) {
  const toneClass = tone === "cyan" ? "text-cyan-300" : "text-slate-200";
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-400">{label}</span>
      <span className={toneClass}>{value}</span>
    </div>
  );
}

/* -----------------------------------------------------------
   DesignViewerDialog — opens from the "VIEW MY DESIGN" button
   on the track page. Shows the approved design image full-size
   with Download / Share / WhatsApp actions so the customer can
   forward it to family without needing us in the loop.
------------------------------------------------------------ */
function DesignViewerDialog({ open, onClose, orderNumber }) {
  const previewUrl = `${API}/public/track/${orderNumber}/design-preview`;
  const trackUrl = `${window.location.origin}/track/${orderNumber}`;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-2xl bg-slate-900 border-slate-700 text-white rounded-sm p-0 overflow-hidden"
        data-testid="design-viewer-dialog"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-800">
          <div className="text-[10px] font-mono-num tracking-widest text-emerald-400">// APPROVED DESIGN</div>
          <DialogTitle className="font-heading text-2xl mt-1">
            Aapka Design — Order {orderNumber}
          </DialogTitle>
          <p className="text-xs text-slate-400 mt-1">
            Design ko download karke ya WhatsApp par family ke saath share kar sakte hain.
          </p>
        </DialogHeader>
        <div className="bg-slate-950 p-3 flex items-center justify-center min-h-[280px] max-h-[60vh] overflow-auto">
          <img
            src={previewUrl}
            alt={`Design for order ${orderNumber}`}
            className="max-w-full max-h-[55vh] object-contain"
            data-testid="design-viewer-image"
            onError={(e) => {
              const parent = e.currentTarget.parentElement;
              e.currentTarget.style.display = "none";
              if (parent && !parent.querySelector("[data-design-fallback]")) {
                const div = document.createElement("div");
                div.setAttribute("data-design-fallback", "1");
                div.className = "text-center px-4 py-8";
                div.innerHTML =
                  '<div style="color:#f87171;font-weight:800;letter-spacing:.1em;font-size:12px">DESIGN LOAD NAHI HO PAYA</div>' +
                  '<div style="color:#94a3b8;font-size:13px;margin-top:8px">Neeche button pe click karke direct download kar lein.</div>';
                parent.appendChild(div);
              }
            }}
          />
        </div>
        <div className="px-5 py-4 border-t border-slate-800 space-y-3">
          <DesignActions
            fileUrl={previewUrl}
            fileName={`design-${orderNumber}.png`}
            orderNumber={orderNumber}
            trackUrl={trackUrl}
          />
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={onClose}
              className="rounded-sm h-9 border-slate-600 text-slate-300 hover:bg-slate-800"
              data-testid="design-viewer-close"
            >
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

